// A section drawing of one indoor unit in its ceiling (or on its wall or
// floor), drawn to scale from src/data/indoorUnits.js: the slab, the unit,
// the false ceiling at the depth to plan for, the air, and the dimension.
// 1 mm = 0.2 px in a 360 × 170 drawing.

import { planMm, planShort } from '../data/indoorUnits';

const K = 0.2;
const S = 22; // slab underside
const BLUE = '#2563eb';
const ORANGE = '#c2410c';
const LABEL = { fontSize: 10, fontWeight: 700, fill: '#475569' };

function arrow(x1, y1, x2, y2, color = BLUE) {
  const a = Math.atan2(y2 - y1, x2 - x1);
  const p = (s) => `${(x2 - 9 * Math.cos(a + s)).toFixed(1)},${(y2 - 9 * Math.sin(a + s)).toFixed(1)}`;
  return (
    <g key={`${x1}-${y1}`}>
      <line x1={x1} y1={y1} x2={x2} y2={y2} stroke={color} strokeWidth="2.5" strokeLinecap="round" />
      <polygon points={`${x2},${y2} ${p(-0.45)} ${p(0.45)}`} fill={color} />
    </g>
  );
}

const Slab = ({ label = 8 }) => (
  <>
    <rect x="0" y="0" width="360" height={S} fill="#e5e7eb" />
    <path d="M8 22L28 0M40 22L60 0M72 22L92 0M104 22L124 0M136 22L156 0M168 22L188 0M200 22L220 0M232 22L252 0M264 22L284 0M296 22L316 0M328 22L348 0" stroke="#cbd5e1" strokeWidth="1.5" />
    <text x={label} y="15" {...LABEL}>SLAB</text>
  </>
);

const Wall = ({ h = 170 }) => <rect x="0" y="0" width="14" height={h} fill="#cbd5e1" />;
const Floor = ({ y = 158 }) => <rect x="0" y={y} width="360" height={170 - y} fill="#e5e7eb" />;
const unitBox = { fill: '#dbeafe', stroke: '#1d4ed8', strokeWidth: 2 };
const Series = ({ x, y, children }) => <text x={x} y={y} textAnchor="middle" fontSize="11" fontWeight="700" fill="#1e3a8a">{children}</text>;

function Dim({ ceilY, text }) {
  const mid = Math.round((S + ceilY) / 2) + 4;
  return (
    <>
      <line x1="332" y1={S} x2="332" y2={ceilY} stroke={ORANGE} strokeWidth="1.5" />
      <line x1="325" y1={S} x2="339" y2={S} stroke={ORANGE} strokeWidth="1.5" />
      <line x1="325" y1={ceilY} x2="339" y2={ceilY} stroke={ORANGE} strokeWidth="1.5" />
      <text x="324" y={mid} textAnchor="end" fontSize="12" fontWeight="800" fill={ORANGE}>{text}</text>
    </>
  );
}

const NoCeiling = ({ x = 8, y = 150, text = 'NO FALSE CEILING NEEDED' }) => (
  <text x={x} y={y} fontSize="11" fontWeight="800" fill="#475569">{text}</text>
);

export default function UnitDiagram({ unit: u, className = '' }) {
  const body = Math.round((u.drawMm || u.unitMm || 0) * K);
  const pm = planMm(u);
  const ceilY = pm ? S + Math.round(pm[1] * K) : 0;
  // Ceiling drawings end just below the air arrows; the rest use the full height.
  const H = pm ? Math.max(112, ceilY + 46) : 170;
  const label = `Section drawing: ${u.name}`;
  let inner = null;

  if (u.kind === 'duct') {
    const unitY = ceilY - 6 - body;
    const plenumY = unitY + Math.max(4, body - 16);
    inner = (
      <>
        <Slab />
        <line x1="124" y1={S} x2="124" y2={unitY} stroke="#64748b" strokeWidth="2" />
        <line x1="216" y1={S} x2="216" y2={unitY} stroke="#64748b" strokeWidth="2" />
        <rect x="110" y={unitY} width="120" height={body} rx="3" {...unitBox} />
        <Series x={170} y={unitY + Math.round(body / 2) + 4}>{u.series}</Series>
        <rect x="230" y={plenumY} width="30" height={ceilY - plenumY} fill="#eff6ff" stroke="#1d4ed8" strokeWidth="1.5" />
        <rect x="0" y={ceilY} width="360" height="5" fill="#94a3b8" />
        <rect x="232" y={ceilY} width="26" height="5" fill="#1d4ed8" />
        <rect x="140" y={ceilY} width="26" height="5" fill="#475569" />
        {unitY - S >= 12 && <text x="8" y={Math.round((S + unitY) / 2) + 4} fontSize="9" fontWeight="700" fill={ORANGE}>SERVICE SPACE</text>}
        <text x="8" y={ceilY + 18} {...LABEL}>FALSE CEILING</text>
        {arrow(246, ceilY + 10, 278, ceilY + 36)}
        {arrow(153, ceilY + 36, 153, ceilY + 10, '#64748b')}
        <Dim ceilY={ceilY} text={planShort(u)} />
      </>
    );
  } else if (u.kind === 'cassette') {
    const y = ceilY - body;
    inner = (
      <>
        <Slab />
        <line x1="146" y1={S} x2="146" y2={y} stroke="#64748b" strokeWidth="2" />
        <line x1="214" y1={S} x2="214" y2={y} stroke="#64748b" strokeWidth="2" />
        <rect x="0" y={ceilY} width="360" height="5" fill="#94a3b8" />
        <rect x="135" y={y} width="90" height={body} rx="3" {...unitBox} />
        <Series x={180} y={y + Math.round(body / 2) + 4}>{u.series}</Series>
        <rect x="120" y={ceilY} width="120" height="7" rx="2" fill="#ffffff" stroke="#1d4ed8" strokeWidth="2" />
        {y - S >= 12 && <text x="8" y={Math.round((S + y) / 2) + 4} fontSize="9" fontWeight="700" fill={ORANGE}>SERVICE SPACE</text>}
        <text x="8" y={ceilY + 18} {...LABEL}>FALSE CEILING</text>
        {arrow(124, ceilY + 12, 92, ceilY + 38)}
        {arrow(236, ceilY + 12, 268, ceilY + 38)}
        <Dim ceilY={ceilY} text={planShort(u)} />
      </>
    );
  } else if (u.kind === 'corner') {
    const gap = Math.round((u.backGapMm || 0) * K);
    const x0 = 14 + gap;
    const y = ceilY - body;
    inner = (
      <>
        <Slab label={170} />
        <Wall h={H} />
        <text x="10" y={H - 26} textAnchor="middle" fontSize="8" fontWeight="700" fill="#475569" transform={`rotate(-90 10 ${H - 26})`}>WALL</text>
        <rect x="112" y={ceilY} width="248" height="5" fill="#94a3b8" />
        <rect x="14" y={ceilY} width="12" height="5" fill="#94a3b8" />
        <rect x={x0} y={y} width="70" height={body} rx="3" {...unitBox} />
        <Series x={x0 + 35} y={y + Math.round(body / 2) + 4}>{u.series}</Series>
        <rect x="24" y={ceilY} width="88" height="6" rx="2" fill="#ffffff" stroke="#1d4ed8" strokeWidth="2" />
        {y - S >= 10 && <text x="118" y={Math.round((S + y) / 2) + 4} fontSize="9" fontWeight="700" fill={ORANGE}>SERVICE SPACE</text>}
        {gap > 0 && (
          <>
            <line x1={x0} y1={ceilY + 6} x2={x0} y2={ceilY + 30} stroke={ORANGE} strokeWidth="1" strokeDasharray="3 2" />
            <line x1="14" y1={ceilY + 26} x2={x0} y2={ceilY + 26} stroke={ORANGE} strokeWidth="1.5" />
            <line x1="14" y1={ceilY + 21} x2="14" y2={ceilY + 31} stroke={ORANGE} strokeWidth="1.5" />
            <line x1={x0} y1={ceilY + 21} x2={x0} y2={ceilY + 31} stroke={ORANGE} strokeWidth="1.5" />
            <text x={x0 + 6} y={ceilY + 30} fontSize="10" fontWeight="800" fill={ORANGE}>3 in at the back</text>
          </>
        )}
        <text x="190" y={ceilY + 18} {...LABEL}>FALSE CEILING</text>
        {arrow(116, ceilY + 10, 164, ceilY + 40)}
        <Dim ceilY={ceilY} text={planShort(u)} />
      </>
    );
  } else if (u.kind === 'susp' || u.kind === 'susp4') {
    const four = u.kind === 'susp4';
    const bottom = 24 + body;
    inner = (
      <>
        <Slab />
        <rect x="110" y="24" width={four ? 140 : 144} height={body} rx={four ? 16 : 10} {...unitBox} />
        <Series x={four ? 180 : 182} y={24 + Math.round(body / 2) + 4}>{u.series}</Series>
        {four ? arrow(106, bottom - 2, 72, bottom + 30) : null}
        {arrow(four ? 254 : 256, bottom - (four ? 2 : 6), four ? 288 : 302, bottom + (four ? 30 : 28))}
        <NoCeiling />
      </>
    );
  } else if (u.kind === 'wall') {
    inner = (
      <>
        <Slab label={160} />
        <Wall />
        <Floor y={160} />
        <rect x="14" y="40" width="52" height="60" rx="8" {...unitBox} />
        <text x="40" y="74" textAnchor="middle" fontSize="10" fontWeight="700" fill="#1e3a8a">{u.series}</text>
        {arrow(64, 100, 106, 132)}
        <NoCeiling x={150} y={80} />
      </>
    );
  } else if (u.kind === 'floor' || u.kind === 'floorhidden') {
    const hidden = u.kind === 'floorhidden';
    inner = (
      <>
        <Slab label={160} />
        <Wall />
        <Floor />
        {hidden && <rect x="14" y="34" width="62" height="124" fill="#ffffff" stroke="#64748b" strokeWidth="2" strokeDasharray="5 4" />}
        <rect x={hidden ? 18 : 14} y={hidden ? 46 : 38} width="44" height={hidden ? 112 : 120} rx={hidden ? 5 : 6} {...unitBox} />
        {hidden && <rect x="22" y="32" width="48" height="5" fill="#1d4ed8" />}
        <text x={hidden ? 40 : 36} y={hidden ? 104 : 102} textAnchor="middle" fontSize="10" fontWeight="700" fill="#1e3a8a">{u.series}</text>
        {hidden ? arrow(46, 28, 92, 20) : arrow(56, 36, 100, 26)}
        {hidden ? (
          <>
            <NoCeiling x={150} y={80} text="HIDDEN IN A BOX" />
            <text x="150" y="96" fontSize="11" fontWeight="800" fill={ORANGE}>AT LEAST 220 mm (9 in) DEEP</text>
          </>
        ) : <NoCeiling x={150} y={80} />}
      </>
    );
  } else if (u.kind === 'ahu') {
    inner = (
      <>
        <Slab />
        <Floor />
        <rect x="14" y="34" width="124" height="124" fill="#ffffff" stroke="#64748b" strokeWidth="2" strokeDasharray="5 4" />
        <text x="76" y="50" textAnchor="middle" {...LABEL}>AHU ROOM</text>
        <rect x="44" y="72" width="64" height="86" rx="4" {...unitBox} />
        <Series x={76} y={120}>{u.series}</Series>
        <rect x="96" y="54" width="254" height="18" fill="#eff6ff" stroke="#1d4ed8" strokeWidth="1.5" />
        <rect x="200" y="72" width="24" height="4" fill="#1d4ed8" />
        <rect x="280" y="72" width="24" height="4" fill="#1d4ed8" />
        {arrow(212, 78, 212, 106)}
        {arrow(292, 78, 292, 106)}
        <text x="196" y="132" fontSize="11" fontWeight="800" fill="#475569">DUCTS TO THE HALL</text>
      </>
    );
  } else if (u.kind === 'cube') {
    inner = (
      <>
        <Slab />
        <line x1="160" y1={S} x2="160" y2="54" stroke="#64748b" strokeWidth="2" />
        <line x1="220" y1={S} x2="220" y2="54" stroke="#64748b" strokeWidth="2" />
        <rect x="146" y="54" width="88" height={body} rx="6" {...unitBox} />
        <Series x={190} y={104}>{u.series}</Series>
        {arrow(236, 90, 282, 112)}
        <NoCeiling />
      </>
    );
  }

  return (
    <svg viewBox={`0 0 360 ${H}`} className={`block h-auto w-full ${className}`} role="img" aria-label={label}>
      {inner}
    </svg>
  );
}
